import * as Calendar from 'expo-calendar';
import { Platform } from 'react-native';

import { DEFAULT_EVENT_DURATION_MINUTES } from '@sixam/shared/event-flow-constants';
import type { Event } from '@/lib/events';
import { icsCalendarContent, icsCalendarFileName } from '@/lib/integrations/calendar-ics';

export type CalendarActionEvent = Pick<
  Event,
  | 'id'
  | 'title'
  | 'description'
  | 'starts_at'
  | 'ends_at'
  | 'place_name'
  | 'place_address'
  | 'place_lat'
  | 'place_lng'
>;

export function calendarEventPayload(event: CalendarActionEvent, timeZone?: string) {
  const location = [event.place_name, event.place_address].filter(Boolean).join(' · ');

  return {
    title: event.title,
    description: event.description,
    startsAt: event.starts_at,
    endsAt: event.ends_at,
    timeZone,
    location,
  };
}

export async function addEventToAppleCalendar(
  event: CalendarActionEvent,
  timeZone?: string
): Promise<void> {
  if (Platform.OS === 'web') {
    throw new Error('Apple Calendar add-event is only available in the native app.');
  }

  const permission = await Calendar.requestCalendarPermissions(Platform.OS === 'ios');
  if (permission.status !== 'granted') {
    throw new Error('Calendar permission was not granted.');
  }

  const options = {
    title: event.title,
    startDate: new Date(event.starts_at),
    endDate: event.ends_at
      ? new Date(event.ends_at)
      : new Date(new Date(event.starts_at).getTime() + DEFAULT_EVENT_DURATION_MINUTES * 60 * 1000),
    notes: event.description ?? undefined,
    location: [event.place_name, event.place_address].filter(Boolean).join(' · '),
    timeZone,
  };

  if (Platform.OS === 'ios') {
    const calendar = Calendar.getDefaultCalendarSync();
    await calendar.addEventWithForm(options);
    return;
  }

  const calendars = await Calendar.getCalendars(Calendar.EntityTypes.EVENT);
  const writable = calendars.find((calendar) => calendar.allowsModifications);
  if (!writable) {
    throw new Error('No writable calendar is available on this device.');
  }

  await writable.addEventWithForm(options);
}

export function downloadIcsCalendarEvent(event: CalendarActionEvent): void {
  if (Platform.OS !== 'web') {
    throw new Error('ICS export is available in the web app.');
  }

  const blob = new Blob([icsCalendarContent(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = icsCalendarFileName(event);
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
